import { useCallback, useEffect, useMemo, useState } from 'react'
import { permissionService, roleService } from '../api/services/role.service'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { SelectPicker } from '../components/ui/select-picker'
import { useAuth } from '../context/AuthContext'
import {
  formatPermissionAction,
  formatPermissionGroupLabel,
  groupPermissions,
} from '../lib/permission-groups'
import { cn } from '../lib/utils'
import type { Permission, Role } from '../types/role'

function samePermissionSet(left: string[], right: string[]) {
  if (left.length !== right.length) {
    return false
  }

  const rightSet = new Set(right)
  return left.every((id) => rightSet.has(id))
}

function roleInitial(label: string) {
  const trimmed = label.trim()
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?'
}

function Switch({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean
  disabled?: boolean
  onChange: () => void
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        'relative h-6 w-11 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600/30 disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-green-600' : 'bg-parch-200',
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform',
          checked && 'translate-x-5',
        )}
      />
    </button>
  )
}

export function RolesPage() {
  const { can } = useAuth()
  const canUpdate = can('roles-update')

  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null)
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([])
  const [baselinePermissionIds, setBaselinePermissionIds] = useState<string[]>([])
  const [roleCoverage, setRoleCoverage] = useState<Record<string, number>>({})
  const [searchQuery, setSearchQuery] = useState('')
  const [moduleFilter, setModuleFilter] = useState<string>('all')
  const [hasLoaded, setHasLoaded] = useState(false)
  const [loadingList, setLoadingList] = useState(true)
  const [loadingRole, setLoadingRole] = useState(false)
  const [saving, setSaving] = useState(false)

  const selectedRole = roles.find((role) => role.id === selectedRoleId) ?? null
  const dirty = !samePermissionSet(selectedPermissionIds, baselinePermissionIds)
  const dirtyCount = useMemo(() => {
    const baseline = new Set(baselinePermissionIds)
    const selected = new Set(selectedPermissionIds)
    let changed = 0
    selectedPermissionIds.forEach((id) => {
      if (!baseline.has(id)) changed += 1
    })
    baselinePermissionIds.forEach((id) => {
      if (!selected.has(id)) changed += 1
    })
    return changed
  }, [selectedPermissionIds, baselinePermissionIds])

  const permissionGroups = useMemo(() => groupPermissions(permissions), [permissions])

  const moduleOptions = useMemo(
    () => [
      { value: 'all', label: 'All modules' },
      ...permissionGroups.map((group) => ({
        value: group.group,
        label: formatPermissionGroupLabel(group.group),
      })),
    ],
    [permissionGroups],
  )

  const normalizedSearch = searchQuery.trim().toLowerCase()

  const visibleGroups = useMemo(() => {
    return permissionGroups
      .filter((group) => moduleFilter === 'all' || group.group === moduleFilter)
      .map((group) => ({
        ...group,
        items: group.items.filter((permission) => {
          if (!normalizedSearch) {
            return true
          }

          const action = formatPermissionAction(permission.name).toLowerCase()
          return (
            action.includes(normalizedSearch) ||
            permission.name.toLowerCase().includes(normalizedSearch)
          )
        }),
      }))
      .filter((group) => group.items.length > 0)
  }, [permissionGroups, moduleFilter, normalizedSearch])

  const coveragePercent = permissions.length
    ? Math.round((selectedPermissionIds.length / permissions.length) * 100)
    : 0

  const loadRolesAndPermissions = useCallback(async () => {
    setLoadingList(true)

    try {
      const [rolesResponse, permissionsResponse] = await Promise.all([
        roleService.list(),
        permissionService.list(),
      ])

      const nextRoles = Array.isArray(rolesResponse.data) ? rolesResponse.data : []
      const nextPermissions = Array.isArray(permissionsResponse.data)
        ? permissionsResponse.data
        : []

      setRoles(nextRoles)
      setPermissions(nextPermissions)

      const coverageEntries = await Promise.all(
        nextRoles.map(async (role) => {
          try {
            const response = await roleService.show(role.id)
            const ids = response.data.permission_ids ?? []
            const percent = nextPermissions.length
              ? Math.round((ids.length / nextPermissions.length) * 100)
              : 0
            return [role.id, percent] as const
          } catch {
            return [role.id, 0] as const
          }
        }),
      )

      setRoleCoverage(Object.fromEntries(coverageEntries))

      setSelectedRoleId((current) => {
        if (current && nextRoles.some((role) => role.id === current)) {
          return current
        }
        return nextRoles[0]?.id ?? null
      })
    } catch {
      setRoles([])
      setPermissions([])
      setRoleCoverage({})
      setSelectedRoleId(null)
    } finally {
      setLoadingList(false)
      setHasLoaded(true)
    }
  }, [])

  const loadRoleDetails = useCallback(async (roleId: string) => {
    setLoadingRole(true)

    try {
      const response = await roleService.show(roleId)
      const permissionIds = response.data.permission_ids ?? []
      setSelectedPermissionIds(permissionIds)
      setBaselinePermissionIds([...permissionIds])
      setRoles((current) =>
        current.map((role) => (role.id === roleId ? { ...role, ...response.data } : role)),
      )
    } catch {
      setSelectedPermissionIds([])
      setBaselinePermissionIds([])
    } finally {
      setLoadingRole(false)
    }
  }, [])

  useEffect(() => {
    void loadRolesAndPermissions()
  }, [loadRolesAndPermissions])

  useEffect(() => {
    if (selectedRoleId) {
      void loadRoleDetails(selectedRoleId)
    }
  }, [selectedRoleId, loadRoleDetails])

  const selectRole = (roleId: string) => {
    if (roleId === selectedRoleId) {
      return
    }

    if (dirty && canUpdate) {
      const leave = window.confirm(
        'You have unsaved permission changes. Switch role and discard them?',
      )
      if (!leave) {
        return
      }
    }

    setSelectedRoleId(roleId)
    setSearchQuery('')
    setModuleFilter('all')
  }

  const togglePermission = (permissionId: string) => {
    if (!canUpdate) {
      return
    }

    setSelectedPermissionIds((current) =>
      current.includes(permissionId)
        ? current.filter((id) => id !== permissionId)
        : [...current, permissionId],
    )
  }

  const toggleGroup = (groupPermissionIds: string[], enable: boolean) => {
    if (!canUpdate) {
      return
    }

    setSelectedPermissionIds((current) => {
      if (enable) {
        return Array.from(new Set([...current, ...groupPermissionIds]))
      }
      return current.filter((id) => !groupPermissionIds.includes(id))
    })
  }

  const handleDiscard = () => {
    setSelectedPermissionIds([...baselinePermissionIds])
  }

  const handleSave = async () => {
    if (!selectedRoleId || !canUpdate || !dirty) {
      return
    }

    setSaving(true)

    try {
      const response = await roleService.updatePermissions(selectedRoleId, {
        permission_ids: selectedPermissionIds,
      })
      const permissionIds = response.data.permission_ids ?? selectedPermissionIds

      setSelectedPermissionIds(permissionIds)
      setBaselinePermissionIds([...permissionIds])
      setRoles((current) =>
        current.map((role) =>
          role.id === selectedRoleId ? { ...role, ...response.data } : role,
        ),
      )
      setRoleCoverage((current) => ({
        ...current,
        [selectedRoleId]: permissions.length
          ? Math.round((permissionIds.length / permissions.length) * 100)
          : 0,
      }))
    } catch {
      // handled globally
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppShell>
      <div className={cn('mx-auto max-w-6xl px-2 py-2', dirty && canUpdate && 'pb-28')}>
        <div className="mb-6">
          <h1 className="font-display text-2xl font-bold">Roles & permissions</h1>
          <p className="mt-1 text-sm text-ink-600">
            Design what each role can do across PeerMed. Super Admin is excluded and always retains
            full access.
          </p>
        </div>

        {loadingList && !hasLoaded ? (
          <p className="text-sm text-ink-500">Loading roles and permissions…</p>
        ) : hasLoaded && roles.length === 0 ? (
          <p className="text-sm text-ink-500">No roles found.</p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {roles.map((role) => {
                const isSelected = role.id === selectedRoleId
                const percent = role.id === selectedRoleId ? coveragePercent : (roleCoverage[role.id] ?? 0)

                return (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => selectRole(role.id)}
                    className={cn(
                      'rounded-xl border bg-white p-4 text-left transition-colors',
                      isSelected
                        ? 'border-green-600 bg-gradient-to-b from-green-50 to-white shadow-[0_0_0_1px_theme(colors.green.600)]'
                        : 'border-parch-100 hover:border-green-200',
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-sm font-bold text-green-700">
                        {roleInitial(role.label)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-ink-900">{role.label}</p>
                        <p className="text-xs text-ink-500">
                          {role.users_count ?? 0} user{(role.users_count ?? 0) === 1 ? '' : 's'}
                          {isSelected ? ' · editing' : ' · editable'}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-parch-100">
                        <div
                          className="h-full rounded-full bg-green-600 transition-all"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="min-w-[2.5rem] text-right text-xs font-bold text-green-700">
                        {percent}%
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2 sm:items-center">
              <Input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search capabilities…"
                aria-label="Search capabilities"
              />
              <SelectPicker
                options={moduleOptions}
                value={moduleFilter}
                onChange={setModuleFilter}
                placeholder="Filter by module"
              />
            </div>

            <div className="mt-4 space-y-3">
              {!selectedRole ? (
                <p className="text-sm text-ink-500">Select a role to manage permissions.</p>
              ) : loadingRole ? (
                <p className="text-sm text-ink-500">Loading role permissions…</p>
              ) : permissions.length === 0 ? (
                <p className="text-sm text-ink-500">
                  No permissions found. Run permissions:sync on the backend.
                </p>
              ) : visibleGroups.length === 0 ? (
                <div className="rounded-xl border border-parch-100 bg-white px-4 py-8 text-center shadow-card">
                  <p className="text-sm font-medium text-ink-800">No capabilities match</p>
                  <p className="mt-1 text-xs text-ink-500">
                    Try another search term or module filter.
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      setSearchQuery('')
                      setModuleFilter('all')
                    }}
                  >
                    Clear filters
                  </Button>
                </div>
              ) : (
                visibleGroups.map((group) => {
                  const groupIds = group.items.map((item) => item.id)
                  const enabledInView = groupIds.filter((id) =>
                    selectedPermissionIds.includes(id),
                  ).length
                  const allEnabled = enabledInView === groupIds.length && groupIds.length > 0

                  return (
                    <section
                      key={group.group}
                      className="overflow-hidden rounded-xl border border-parch-100 bg-white shadow-card"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-parch-100 bg-parch-50/80 px-4 py-3">
                        <div>
                          <h2 className="text-sm font-semibold text-ink-800">
                            {formatPermissionGroupLabel(group.group)}
                          </h2>
                          <p className="text-xs text-ink-500">
                            {enabledInView} of {groupIds.length} enabled for {selectedRole.label}
                          </p>
                        </div>
                        {canUpdate && (
                          <button
                            type="button"
                            className="text-xs font-semibold text-green-700 hover:text-green-800"
                            onClick={() => toggleGroup(groupIds, !allEnabled)}
                          >
                            {allEnabled ? 'Disable All' : 'Enable All'}
                          </button>
                        )}
                      </div>

                      <div className="space-y-1 p-3">
                        {group.items.map((permission) => {
                          const checked = selectedPermissionIds.includes(permission.id)

                          return (
                            <div
                              key={permission.id}
                              className={cn(
                                'flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 transition-colors',
                                checked
                                  ? 'border-green-100 bg-green-50'
                                  : 'border-transparent hover:bg-parch-50',
                              )}
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-ink-800">
                                  {formatPermissionAction(permission.name)}
                                </p>
                                <p className="truncate text-xs text-ink-500">{permission.name}</p>
                              </div>
                              <Switch
                                checked={checked}
                                disabled={!canUpdate || saving}
                                label={`${selectedRole.label}: ${formatPermissionAction(permission.name)}`}
                                onChange={() => togglePermission(permission.id)}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </section>
                  )
                })
              )}
            </div>
          </>
        )}
      </div>

      {dirty && canUpdate && selectedRole && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-green-900/20 bg-[rgba(13,43,26,0.94)] text-white backdrop-blur-sm md:left-56">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
            <p className="text-sm">
              Unsaved changes · {selectedRole.label}
              {dirtyCount > 0
                ? ` · ${dirtyCount} ${dirtyCount === 1 ? 'capability' : 'capabilities'} updated`
                : ''}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="border-white/30 bg-transparent text-white hover:bg-white/10"
                disabled={saving}
                onClick={handleDiscard}
              >
                Discard
              </Button>
              <Button type="button" size="sm" disabled={saving} onClick={() => void handleSave()}>
                {saving ? 'Saving…' : 'Save Permissions'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  )
}
